import * as Sentry from '@sentry/react';

import { isCheckoutInputError } from './checkout-input-error';
import { getHttpStatus } from './get-http-status';
import { isSellerNotActiveError } from './payments-availability';

/**
 * Marca no Sentry os erros do checkout para a regra de alerta mandar ao `#alertas-checkout`
 * (pedido do Bryan, 28/10/2026). Mesmas tags do pay (`flow`, `checkout_stage`, `ref`), para o
 * canal ler front e back do mesmo jeito.
 */

/** Página do checkout pela URL (com ou sem o `/:platform` na frente). */
export function checkoutPageOf(pathname: string): string {
	const path = String(pathname || '/').replace(
		/^\/[^/]+(?=\/(p|planos)\/)/,
		''
	);
	if (/^\/p\/thanks\//.test(path)) return 'thanks';
	if (/^\/p\/(upsell|downsell)\//.test(path)) return 'upsell';
	if (/^\/p\/recurring\/pix\//.test(path)) return 'recurring-pix';
	if (/^\/p\/recurring\//.test(path)) return 'recurring';
	if (/^\/p\//.test(path)) return 'single';
	if (/^\/planos\//.test(path)) return 'pricing-page';
	if (/^\/credit-cards\/create/.test(path)) return 'card';
	if (/^\/cp\/subscriptions\/[^/]+\/card/.test(path)) return 'card';
	return 'other';
}

/** `Ref. ERR-xxxxxxxx` que o pay devolve no 500 (curseduca-pay#629). */
export function refOf(message: unknown): string | null {
	const m =
		typeof message === 'string'
			? message.match(/\bERR-[0-9a-f]{8}\b/i)
			: null;
	return m ? m[0] : null;
}

type EventLike = { level?: string; tags?: Record<string, unknown> };

/**
 * Para o `beforeSend`: marca `flow: checkout` + a página. Só erro (`error`/`fatal`; sem nível, o
 * Sentry trata como erro) ou evento que já veio com `checkout_stage`, e só em página do checkout —
 * aviso de código alheio (warning/info) ou de rota fora do fluxo não pode tocar o #alertas-checkout.
 */
export function tagCheckoutEvent<E extends EventLike>(
	event: E,
	pathname: string
): E {
	const page = checkoutPageOf(pathname);
	if (page === 'other') return event;
	const isError =
		event.level === undefined ||
		event.level === 'error' ||
		event.level === 'fatal';
	if (!isError && !event.tags?.['checkout_stage']) return event;
	return {
		...event,
		tags: {
			...(event.tags ?? {}),
			flow: 'checkout',
			checkout_page: page,
			...(event.tags?.['checkout_stage']
				? {}
				: { checkout_stage: 'front' })
		}
	};
}

/**
 * Axios que estourou o tempo esperando o servidor (`ECONNABORTED` do `ontimeout`, ou `ETIMEDOUT` com
 * `clarifyTimeoutError`). Diferente das outras falhas sem resposta (DNS, CORS, bloqueador, offline),
 * é lentidão do servidor e vai ao Sentry. O axios 0.24 usa `ECONNABORTED` também para o
 * "Request aborted" (o navegador cancelou, p.ex. ao sair da página) — esse fica de fora.
 */
export function isAxiosTimeout(error: unknown): boolean {
	const e = error as {
		isAxiosError?: boolean;
		code?: unknown;
		message?: unknown;
		response?: unknown;
	};
	if (e?.isAxiosError !== true || e.response != null) return false;
	if (e.code === 'ETIMEDOUT') return true;
	return e.code === 'ECONNABORTED' && e.message !== 'Request aborted';
}

/** Rota da API sem ids: `https://…/payments/123/retry` → `/payments/:id/retry`. */
function apiPath(url: unknown): string | null {
	if (typeof url !== 'string' || !url) return null;
	let path = url;
	try {
		path = new URL(url, 'https://x').pathname;
	} catch {
		path = url.split('?')[0];
	}
	return path.replace(
		/\/(\d+|[0-9a-f]{8}-[0-9a-f-]{27}|[A-Za-z0-9_=-]{20,})(?=\/|$)/g,
		'/:id'
	);
}

/**
 * O comprador clicou em "Finalizar compra" e viu um erro. Hoje isso só virava toast. Vai ao Sentry
 * quando é do servidor (5xx, sem status ou timeout): recusa e validação (4xx) são esperadas — o
 * `beforeSend` já as descarta — e falha de rede do aparelho não tem o que consertar aqui.
 */
export function reportCheckoutSubmitError(
	error: unknown,
	shownMessage: string
): void {
	reportCheckoutError(error, 'submit', shownMessage);
}

/**
 * Erro TRATADO num passo do checkout (o `catch` mostra toast, cai no padrão ou segue). Mesma regra do
 * envio: vai quando é do servidor (5xx, sem status ou timeout); 4xx, falha de rede do aparelho e erro
 * do que o comprador digitou (`CheckoutInputError`), não.
 */
export function reportCheckoutError(
	error: unknown,
	stage: string,
	shownMessage?: string
): void {
	const e = error as {
		isAxiosError?: boolean;
		response?: { status?: number; data?: { message?: unknown } } | null;
		config?: { url?: string; method?: string };
	};
	// Cartão vencido, dado de cartão recusado: o comprador corrige, não há o que consertar aqui.
	if (isCheckoutInputError(error)) return;
	const timeout = isAxiosTimeout(error);
	if (e?.isAxiosError && e.response == null && !timeout) return;
	// Vendedor em análise ou pausado (409 `seller_not_active`): estado de negócio esperado.
	if (isSellerNotActiveError(error)) return;
	const status = getHttpStatus(error);
	if (status !== null && status < 500) return;

	const ref = refOf(e?.response?.data?.message) ?? refOf(shownMessage);
	const path = apiPath(e?.config?.url);
	Sentry.captureException(error, {
		level: 'error',
		tags: {
			flow: 'checkout',
			checkout_stage: stage,
			...(status !== null ? { http_status: String(status) } : {}),
			...(timeout ? { network: 'timeout' } : {}),
			...(path
				? {
						checkout_route: `${String(
							e?.config?.method ?? ''
						).toUpperCase()} ${path}`.trim()
				  }
				: {}),
			...(ref ? { ref } : {})
		},
		extra: shownMessage ? { shownMessage } : {}
	});
}
